import React, { useEffect, useState } from "react";
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, InputLabel, MenuItem, Switch } from "@mui/material";
import moment from "moment";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { get, post } from "utils/WebService";
import { maskPhoneWithDdd } from "utils/Functions";
import { coresDoTema } from "assets/coresTema";
import SeletorMarcacoes from "./SeletorMarcacoes";
import { ehCarteiraDeCobranca } from "utils/Carteiras";

const TIPOS_HONORARIO = [
    { id: 1, nome: "Sucumbenciais" },
    { id: 2, nome: "Contratuais" },
    { id: 3, nome: "Iniciais" },
    { id: 4, nome: "Finais" },
    { id: 5, nome: "Outros" },
];

const rotulo = { px: 1, pb: 0.5, pt: 0.2 };

const formularioVazio = () => ({
    carteiraId: "",
    statusId: "",
    usuarioId: "",
    marcacoesIds: [],
    tipoHonorario: 5,
    titulo: "Outros",
    valor: "",
    // Data local (o toISOString dava o dia seguinte depois das 21h)
    vencimento: moment().format("YYYY-MM-DD"),
    descricao: "",
    isTerceiro: false,
    nomeTerceiro: "",
    documentoTerceiro: "",
    codigoClienteTerceiro: "",
    telefoneTerceiro: "",
    emailTerceiro: "",
});

/** "1.234,56", "1234,56" ou "1234.56" -> "1234.56"; vazio ou inválido -> null. */
const valorParaEnvio = (texto) => {
    const t = String(texto || "").trim().replace(/[R$\s]/g, "");
    if (!t) return null;
    const normal = t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t;
    return /^\d+(\.\d{1,2})?$/.test(normal) ? normal : null;
};

/**
 * Nova ficha de honorários com o débito, do devedor ou de um terceiro (embargos de terceiro: o terceiro vira um
 * devedor ligado a este, com a ficha e o débito nele). Grava em /devedor/honorario-avulso/save; erro fica na janela.
 */
export default function NovaFichaHonorariosDialog({ open, onClose, recordId, carteiraInicialNome, onSalvo }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);

    const [form, setForm] = useState(formularioVazio());
    const [carteiras, setCarteiras] = useState([]);
    const [carteiraTravada, setCarteiraTravada] = useState(false);
    const [status, setStatus] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState("");

    const altera = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));

    useEffect(() => {
        if (!open) return;
        setForm(formularioVazio());
        setErro("");
        get("/carteira/usuario-contexto").then(res => {
            const lista = (res?.carteiras || []).filter(ehCarteiraDeCobranca);
            setCarteiras(lista);
            setCarteiraTravada(!!res?.isLocked);
            const inicial = (res?.isLocked && res?.defaultCarteiraId)
                || lista.find(c => carteiraInicialNome && c.nome === carteiraInicialNome)?.id
                || (lista.length === 1 ? lista[0].id : "");
            setForm(prev => ({ ...prev, carteiraId: inicial || "" }));
        }).catch(() => setCarteiras([]));
        get("/status-ficha/list-all").then(res => setStatus(res?.list || res?.data || (Array.isArray(res) ? res : []))).catch(() => {});
        get("/users/list-all-operators").then(res => setUsuarios(res?.list || res?.data || (Array.isArray(res) ? res : []))).catch(() => {});
        // A carteira da ficha aberta só vale como sugestão ao abrir a janela
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const salvar = () => {
        const valor = valorParaEnvio(form.valor);
        if (!form.carteiraId) return setErro("Escolha a carteira da ficha de honorários.");
        if (!valor || Number(valor) <= 0) return setErro("Informe o valor do débito (ex.: 1.500,00).");
        if (form.isTerceiro) {
            if (!form.nomeTerceiro.trim()) return setErro("Informe o nome do terceiro (embargante).");
            if (!form.documentoTerceiro.trim() && !form.codigoClienteTerceiro.trim()) {
                return setErro("Informe o CPF/CNPJ ou o código do terceiro.");
            }
        }
        setErro("");
        setSalvando(true);
        post("/devedor/honorario-avulso/save", {
            idDevedor: recordId,
            carteiraId: form.carteiraId,
            statusId: form.statusId || null,
            usuarioId: form.usuarioId || null,
            marcacoesIds: form.marcacoesIds,
            titulo: form.titulo,
            valorOriginal: valor,
            vencimento: form.vencimento,
            tipoHonorario: form.tipoHonorario,
            descricao: form.descricao,
            isTerceiro: form.isTerceiro,
            nomeTerceiro: form.nomeTerceiro,
            documentoTerceiro: form.documentoTerceiro,
            codigoClienteTerceiro: form.codigoClienteTerceiro.trim(),
            telefoneTerceiro: form.telefoneTerceiro ? form.telefoneTerceiro.replace(/\D/g, "") : null,
            emailTerceiro: form.emailTerceiro,
        })
            .then(res => {
                setSalvando(false);
                onSalvo && onSalvo(res || {});
            })
            .catch(err => {
                setSalvando(false);
                setErro(err?.message || "Não foi possível cadastrar a ficha de honorários.");
            });
    };

    return (
        <Dialog open={open} onClose={() => !salvando && onClose()} maxWidth="md" fullWidth
            PaperProps={{ sx: { backgroundColor: cores.cartao } }}>
            <DialogTitle>Nova Ficha de Honorários</DialogTitle>
            <DialogContent dividers sx={{ borderColor: cores.divisoria }}>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12}>
                        <MDBox px={1.5} py={1} sx={{ borderRadius: 1, border: `1px solid ${cores.borda}`, backgroundColor: cores.painel }}>
                            <FormControlLabel
                                control={<Switch checked={form.isTerceiro} onChange={e => altera("isTerceiro", e.target.checked)} color="info" />}
                                label={<MDTypography variant="button" fontWeight="medium">Honorários cobrados de terceiro (ex.: embargos de terceiro)</MDTypography>}
                                sx={{ m: 0 }}
                            />
                            <MDTypography variant="caption" color="text" display="block">
                                Ligado: o terceiro é cadastrado como devedor ligado a este, com a ficha de honorários e o débito nele.
                                Desligado: a ficha de honorários é deste devedor.
                            </MDTypography>
                        </MDBox>
                    </Grid>

                    {form.isTerceiro && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <InputLabel sx={rotulo}>Nome do terceiro *</InputLabel>
                                <MDInput fullWidth size="small" value={form.nomeTerceiro} onChange={e => altera("nomeTerceiro", e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={rotulo}>CPF/CNPJ</InputLabel>
                                <MDInput fullWidth size="small" value={form.documentoTerceiro} onChange={e => altera("documentoTerceiro", e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={rotulo}>Código do cliente</InputLabel>
                                <MDInput fullWidth size="small" value={form.codigoClienteTerceiro} onChange={e => altera("codigoClienteTerceiro", e.target.value)} />
                                <MDTypography variant="caption" color="text" display="block" px={1}>Em branco: o CPF/CNPJ.</MDTypography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputLabel sx={rotulo}>Telefone</InputLabel>
                                <MDInput fullWidth size="small" value={form.telefoneTerceiro} placeholder="(00) 00000-0000"
                                    onChange={e => altera("telefoneTerceiro", maskPhoneWithDdd(e.target.value))} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputLabel sx={rotulo}>E-mail</InputLabel>
                                <MDInput fullWidth size="small" value={form.emailTerceiro} onChange={e => altera("emailTerceiro", e.target.value)} />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12} sm={3}>
                        <InputLabel sx={rotulo}>Carteira *</InputLabel>
                        <MDInput select fullWidth size="small" value={form.carteiraId}
                            onChange={e => setForm(prev => ({ ...prev, carteiraId: e.target.value, marcacoesIds: [] }))}
                            disabled={carteiraTravada || carteiras.length === 1}>
                            {carteiras.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
                        </MDInput>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <InputLabel sx={rotulo}>Status da ficha</InputLabel>
                        <MDInput select fullWidth size="small" value={form.statusId} onChange={e => altera("statusId", e.target.value)}
                            SelectProps={{ displayEmpty: true }}>
                            <MenuItem value="">Sem status</MenuItem>
                            {status.map(s => <MenuItem key={s.id} value={s.id}>{s.nome || s.ccNome}</MenuItem>)}
                        </MDInput>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <InputLabel sx={rotulo}>Responsável</InputLabel>
                        <MDInput select fullWidth size="small" value={form.usuarioId} onChange={e => altera("usuarioId", e.target.value)}
                            SelectProps={{ displayEmpty: true }}>
                            <MenuItem value="">Eu (quem está cadastrando)</MenuItem>
                            {usuarios.map(u => <MenuItem key={u.id} value={u.id}>{u.nome}</MenuItem>)}
                        </MDInput>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <InputLabel sx={rotulo}>Marcação especial</InputLabel>
                        <SeletorMarcacoes carteira={form.carteiraId} alvos={[1, 3]} placeholder="Selecione..."
                            value={form.marcacoesIds} onChange={ids => altera("marcacoesIds", ids)} />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <InputLabel sx={rotulo}>Tipo de honorário</InputLabel>
                        <MDInput select fullWidth size="small" value={form.tipoHonorario}
                            onChange={e => {
                                const tipo = TIPOS_HONORARIO.find(t => t.id === e.target.value);
                                setForm(prev => ({ ...prev, tipoHonorario: e.target.value, titulo: tipo ? tipo.nome : prev.titulo }));
                            }}>
                            {TIPOS_HONORARIO.map(t => <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>)}
                        </MDInput>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel sx={rotulo}>Valor do débito (R$) *</InputLabel>
                        <MDInput fullWidth size="small" value={form.valor} placeholder="0,00" onChange={e => altera("valor", e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel sx={rotulo}>Vencimento</InputLabel>
                        <MDInput fullWidth size="small" type="date" value={form.vencimento} onChange={e => altera("vencimento", e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel sx={rotulo}>Observação</InputLabel>
                        <MDInput fullWidth multiline rows={2} value={form.descricao} onChange={e => altera("descricao", e.target.value)} />
                    </Grid>

                    {erro && (
                        <Grid item xs={12}>
                            <MDTypography variant="caption" color="error" fontWeight="medium">{erro}</MDTypography>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={salvando}>Cancelar</MDButton>
                <MDButton variant="contained" color="info" onClick={salvar} disabled={salvando}>
                    {salvando ? <CircularProgress size={18} color="inherit" /> : "Salvar"}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
}
